import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for handling drag functionality
 * @param {Object} position - Current position {x, y}
 * @param {Function} onPositionChange - Callback when position changes during drag
 * @param {Object} containerRef - Ref to the draggable container element
 * @param {Object} options - Configuration options
 * @param {Function} options.onDragStart - Optional callback when drag starts
 * @param {Function} options.onDragEnd - Optional callback when drag ends
 * @param {Object} options.bounds - Optional bounds for constraining drag {left, top, right, bottom}
 * @param {Object} options.editorBoundary - Editor boundary { top, bottom } to constrain within
 * @returns {Object} - Drag state and handlers
 */
export function useDraggable(position, onPositionChange, containerRef, options = {}) {
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });
  const [userHasDragged, setUserHasDragged] = useState(false);

  const {
    onDragStart: onDragStartCallback,
    onDragEnd: onDragEndCallback,
    bounds = { left: 10, top: 10, right: 10, bottom: 10 },
    editorBoundary
  } = options;

  // Handle drag start
  const handleDragStart = (e) => {
    if (!position || !containerRef.current) return;

    e.preventDefault(); // Prevent text selection
    setIsDragging(true);

    if (onDragStartCallback) {
      onDragStartCallback();
    }

    const offsetX = e.clientX - position.x;
    const offsetY = e.clientY - position.y;
    setDragOffset({ x: offsetX, y: offsetY });
  };

  // Handle drag move
  useEffect(() => {
    if (!isDragging) return;

    const handleDragMove = (e) => {
      e.preventDefault();
      const newX = e.clientX - dragOffset.x;
      const newY = e.clientY - dragOffset.y;

      // Constrain within viewport
      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;

      const constrainedX = Math.max(bounds.left, Math.min(newX, viewportWidth - rect.width - bounds.right));
      let constrainedY = Math.max(bounds.top, Math.min(newY, viewportHeight - rect.height - bounds.bottom));

      // Apply editor boundary constraints if provided
      if (editorBoundary) {
        if (constrainedY < editorBoundary.top + bounds.top) {
          constrainedY = editorBoundary.top + bounds.top;
        }
        if (constrainedY + rect.height > editorBoundary.bottom - bounds.bottom) {
          constrainedY = editorBoundary.bottom - rect.height - bounds.bottom;
        }
      }

      const newPosition = { x: constrainedX, y: constrainedY };

      // Update position (components will handle their own state)
      if (onPositionChange) {
        onPositionChange(newPosition);
      }
    };

    const handleDragEnd = () => {
      setIsDragging(false);
      setUserHasDragged(true); // Mark that user has manually positioned

      if (onDragEndCallback) {
        onDragEndCallback();
      }
    };

    document.addEventListener('mousemove', handleDragMove);
    document.addEventListener('mouseup', handleDragEnd);

    return () => {
      document.removeEventListener('mousemove', handleDragMove);
      document.removeEventListener('mouseup', handleDragEnd);
    };
  }, [isDragging, dragOffset, onPositionChange, onDragEndCallback, bounds, editorBoundary]);

  return {
    isDragging,
    userHasDragged,
    handleDragStart,
    setUserHasDragged
  };
}
